import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit"
import { getBaseEndpoint } from "../../features/endpoints/endpointManager"
import axios from "axios"
import { editablePracticeStates } from "../../features/practiceProfile/practiceProfileProcessSlice"
import { PracticeStatus } from "../Practice/schema"
import { ProviderStatusDisplayId } from "../Provider/schema"
import { RootState } from "../../reduxStore/store"
import { DataStatus } from "../enums"
import { UserReturn } from "./schema"
import { apiService } from "../../features/shared/apiService"
import appInsightsLogger from "../../features/logging/appInsightsLogger"
import { clearHandoffCaseLocalStorage } from "../../features/shared/utils"
import {
  CACHE_KEY_CASE_ID,
  CACHE_KEY_REFERRAL_PACKET,
} from "./userSliceCacheKeys"

export interface SiteOutageMessageState {
  externalId: string
  message: string
  isUserAccessDisabled: boolean
  isEnabled: boolean
}

export enum UserAccountStatusEnum {
  ACTIVE = "active",
  PENDING = "pending-reg.",
}
export const UserRoles: Record<string, UserRole> = {
  ROLE_HA_ADMIN: "role-ha-admin",
  ROLE_AFFILIATE_RELATIONS: "role-affiliate-relations",
  ROLE_EAP_RESEARCH: "role-eap-research",
  ROLE_AFFILIATE: "role-affiliate",
  ROLE_SECURE_CASE: "role-secure-case",
  ROLE_HA_SUPPORT: "role-ha-support",
  ROLE_EAP_BILLING: "role-eap-billing",
  ROLE_CARE_MANAGER: "role-care-manager",
}
export type UserRole =
  | "role-ha-admin"
  | "role-affiliate-relations"
  | "role-eap-research"
  | "role-affiliate"
  | "role-secure-case"
  | "role-ha-support"
  | "role-eap-billing"
  | "role-care-manager"
// TODO: replace all uses of this with the enum below
export type PracticeRole =
  | "practice-admin"
  | "practice-manager"
  | "practice-billing"
  | "provider"
export enum PracticeRoleEnum {
  PRACTICE_ADMIN = "practice-admin",
  PRACTICE_MANAGER = "practice-manager",
  PRACTICE_BILLING = "practice-billing",
  PROVIDER = "provider",
}
export enum UserOnboardingStepsEnum {
  PRE_ONBOARDING = "pre-onboarding",
  GETTING_STARTED = "getting-started",
  PRACTICE_SETUP = "practice-setup",
  PROVIDER_SETUP = "provider-setup",
  CONDITIONAL_REQUIREMENTS = "conditional-requirements",
  ACTIVATION = "activation",
  OFFICE_SETUP = "office-setup",
  TAX_INFORMATION = "tax-info",
}
export const validUserRoles: UserRole[] = [
  "role-ha-admin",
  "role-affiliate-relations",
  "role-eap-research",
  "role-affiliate",
]
export const validPracticeRoles: PracticeRole[] = [
  "practice-admin",
  "practice-manager",
  "practice-billing",
  "provider",
]
export const readOnlyAuthRoles: UserRole[] = [
  "role-affiliate-relations",
  "role-eap-research",
]
export const haUserRoles: UserRole[] = [
  "role-ha-admin",
  "role-affiliate-relations",
  "role-eap-research",
]

const AFFILIATE_ROLE = "role-affiliate"

export interface PracticeRoleSet {
  practiceId: string
  roles: PracticeRole[]
}

interface UserDataStatus {
  practiceRolesDataStatus: DataStatus
  userRecordDataStatus: DataStatus
  userOnboardingJoinPracticeDataStatus: DataStatus
}

export interface UserState {
  id: string
  touAcceptDateTime: string
  touFilename: string
  givenName: string
  surname: string
  name: string
  email: string
  roles: UserRole[]
  handoffCaseId: string
  practiceRoles: PracticeRoleSet[]
  dataStatus: UserDataStatus
  completedUserOnboardingSteps?: UserOnboardingStepsEnum[]
  siteOutageMessage: SiteOutageMessageState | null
}

export const initialUserState: UserState = {
  id: "",
  touAcceptDateTime: "",
  touFilename: "",
  givenName: "",
  surname: "",
  name: "",
  email: "",
  roles: [],
  // restore from local to persist across browser windows
  handoffCaseId: localStorage.getItem(CACHE_KEY_CASE_ID) ?? "",
  practiceRoles: [],
  dataStatus: {
    practiceRolesDataStatus: DataStatus.LOADING,
    userRecordDataStatus: DataStatus.LOADING,
    userOnboardingJoinPracticeDataStatus: DataStatus.LOADING,
  },
  completedUserOnboardingSteps: [],
  siteOutageMessage: null,
}

const getUserEndpoint = (): string => {
  return getBaseEndpoint() + `/account/users`
}

const getAllPracticeRolesEndpoint = (userId: string): string => {
  return getBaseEndpoint() + `/practice/practices/allroles/${userId}`
}

export const userHandoff = createAsyncThunk(
  "user/userHandoff",
  async (handoffState: string, { dispatch }) => {
    dispatch(userSlice.actions.receiveHandoff(handoffState))
    // clear the case local storage before setting new values
    clearHandoffCaseLocalStorage()

    if (handoffState) {
      localStorage.setItem(CACHE_KEY_CASE_ID, handoffState)
      // Fire-and-forget, no blocking
      apiService.referral.get
        .REFERRAL_PACKET_BY_CASEID(handoffState)
        .then((referralPacketResponse) => {
          localStorage.setItem(
            CACHE_KEY_REFERRAL_PACKET,
            JSON.stringify(referralPacketResponse),
          )
        })
        .catch(() => {
          // eat
        })
    }
  },
)

/**
 * Async thunk to update completed onboarding steps for a user.
 * This function maintains all previously completed steps, adds new ones, and ensures no duplicates.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.userId - The ID of the user to update.
 * @param {UserOnboardingStepsEnum[]} params.completedOnboardingSteps - The new onboarding steps to be marked as completed.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 *
 * @throws {Error} If there's an error updating the onboarding steps.
 */
export const putCompletedOnboardingStepsAndUpdateUser = createAsyncThunk(
  "user/completedUserOnboardingSteps",
  async (
    {
      userId,
      completedOnboardingSteps,
      overwrite = false,
      setDataStatus,
    }: {
      userId: string
      completedOnboardingSteps: UserOnboardingStepsEnum[]
      overwrite?: boolean
      setDataStatus?: (status: DataStatus) => void
    },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const previousCompletedOnboardingSteps =
      state.user.completedUserOnboardingSteps || []

    const newCompletedOnboardingSteps = overwrite
      ? completedOnboardingSteps
      : Array.from(
          new Set([
            ...previousCompletedOnboardingSteps,
            ...completedOnboardingSteps,
          ]),
        )

    setDataStatus?.(DataStatus.SENDING)

    try {
      await apiService.account.put.COMPLETED_ONBOARDING_STEPS({
        userId,
        completedOnboardingSteps: newCompletedOnboardingSteps,
      })
      dispatch(
        userSlice.actions.updateCompletedOnboardingSteps(
          newCompletedOnboardingSteps,
        ),
      )

      setDataStatus?.(DataStatus.LOADED)
      return true
    } catch (error) {
      appInsightsLogger.traceError(
        "Error updating users completed onboarding steps:",
        error,
      )
      setDataStatus?.(DataStatus.ERROR)
      return false
    }
  },
)

export const getUserRecord = createAsyncThunk(
  "user/get",
  async (userId: string | undefined, { dispatch }) => {
    if (userId) {
      // retrieve user record
      try {
        dispatch(
          dataStatusUpdated({
            userRecordDataStatus: DataStatus.SENDING,
          }),
        )
        const url: string = getUserEndpoint() + `/${userId}`
        const response = await axios.get<UserReturn>(url)

        if (response.status === 200) {
          const user = response.data
          const payload = {
            id: user.id,
            touAcceptDateTime: user.touAcceptDateTime,
            touFilename: user.touFilename,
            givenName: user.givenName,
            surname: user.surname,
            name: `${user.givenName} ${user.surname}`,
            email: user.email,
            roles: user.userRoles.$values,
            completedUserOnboardingSteps: user.completedOnboardingSteps.$values,
          }
          dispatch(updateUser(payload))
          dispatch(
            dataStatusUpdated({
              userRecordDataStatus: DataStatus.LOADED,
            }),
          )
        } else {
          dispatch(
            dataStatusUpdated({
              userRecordDataStatus: DataStatus.ERROR,
            }),
          )
        }
      } catch (error) {
        console.error(error)
        dispatch(
          dataStatusUpdated({
            userRecordDataStatus: DataStatus.ERROR,
          }),
        )
      }
    } else {
      dispatch(updateUser(initialUserState))
    }
  },
)

interface PracticeRoleResponse {
  id: string
  roles: PracticeRole[]
}
export const getPracticeRoles = createAsyncThunk(
  "practice/getAllRoles",
  async ({ userId }: { userId: string }, { dispatch }) => {
    try {
      dispatch(
        dataStatusUpdated({ practiceRolesDataStatus: DataStatus.SENDING }),
      )
      const url: string = getAllPracticeRolesEndpoint(userId)
      const response = await axios.get<PracticeRoleResponse[]>(url)
      if (response.status === 200) {
        const rolesToAdd = response.data.map((role) => ({
          practiceId: role.id,
          roles: role.roles,
        }))
        dispatch(updatePracticeRoles(rolesToAdd))
        dispatch(
          dataStatusUpdated({ practiceRolesDataStatus: DataStatus.LOADED }),
        )
      }
    } catch (error) {
      console.error(error)
      dispatch(dataStatusUpdated({ practiceRolesDataStatus: DataStatus.ERROR }))
    }
  },
)

const userSlice = createSlice({
  name: "user",
  initialState: initialUserState,
  reducers: {
    updateUser: (state, action) => {
      state.id = action.payload.id
      state.touAcceptDateTime = action.payload.touAcceptDateTime
      state.touFilename = action.payload.touFilename
      state.email = action.payload.email
      state.givenName = action.payload.givenName
      state.surname = action.payload.surname
      state.name = action.payload.name
      state.roles = action.payload.roles
      state.completedUserOnboardingSteps =
        action.payload.completedUserOnboardingSteps
    },
    receiveHandoff: (state, action) => {
      state.handoffCaseId = action.payload.handoffCaseId
    },
    updatePracticeRoles: (
      state: { practiceRoles: PracticeRoleSet[] },
      { payload }: PayloadAction<PracticeRoleSet[]>,
    ) => {
      state.practiceRoles = [...payload]
    },
    updateCompletedOnboardingSteps: (
      state: UserState,
      { payload }: PayloadAction<UserOnboardingStepsEnum[]>,
    ) => {
      state.completedUserOnboardingSteps = payload
    },
    dataStatusUpdated: (
      state: { dataStatus: UserDataStatus },
      { payload }: PayloadAction<Partial<UserDataStatus>>,
    ) => {
      state.dataStatus = {
        ...state.dataStatus,
        ...payload,
      }
    },
    updateSiteOutageMessage: (
      state: UserState,
      { payload }: PayloadAction<SiteOutageMessageState | null>,
    ) => {
      state.siteOutageMessage = payload
    },
  },
})

export const {
  updateUser,
  updatePracticeRoles,
  updateCompletedOnboardingSteps,
  dataStatusUpdated,
  updateSiteOutageMessage,
} = userSlice.actions

export const getSiteOutageMessage = createAsyncThunk(
  "user/getSiteOutageMessage",
  async (_, { dispatch }) => {
    const message = await apiService.account.get.SITE_OUTAGE_MESSAGE()
    dispatch(updateSiteOutageMessage(message))
    return message
  },
)

export const selectUserDataStatus = (state: {
  user: UserState
}): UserDataStatus => state.user.dataStatus

export const selectCurrentUserRoles = (state: {
  user: UserState
}): UserRole[] => state.user.roles

export const selectHandoffCaseId = (state: { user: UserState }) =>
  state.user.handoffCaseId

export const selectIsAffiliateRelations = (state: {
  user: UserState
}): boolean => state.user.roles.includes("role-affiliate-relations")

export const selectIsAROrEapResearch = (state: { user: UserState }): boolean =>
  state.user.roles.includes("role-affiliate-relations") ||
  state.user.roles.includes("role-eap-research")

export const selectIsCareManager = (state: { user: UserState }): boolean =>
  state.user.roles.includes("role-care-manager")

export const selectIsEapBillingUser = (state: { user: UserState }): boolean =>
  state.user.roles.includes("role-eap-billing")

export const selectIsSecureHAUser = (state: { user: UserState }): boolean =>
  state.user.roles.includes("role-secure-case") &&
  (state.user.roles.includes("role-affiliate-relations") ||
    state.user.roles.includes("role-eap-research"))

export const selectIsAffiliate = (state: RootState): boolean =>
  state.user.roles.includes("role-affiliate")

export const selectIsRoleHASupport = (state: { user: UserState }): boolean =>
  state.user.roles.includes("role-ha-support")

export const selectAllPracticeRoles = (state: {
  user: UserState
}): PracticeRoleSet[] => state.user.practiceRoles

export const selectPracticeRolesForPractice =
  (practiceId: string) =>
  (state: { user: UserState }): PracticeRole[] => {
    let practiceRoles: PracticeRole[] = []
    for (const role of state.user.practiceRoles) {
      if (role.practiceId === practiceId) {
        practiceRoles = role.roles as PracticeRole[]
      }
    }
    return practiceRoles
  }

export const selectHasEditAuth = (practiceId: string, adminCanEdit = false) =>
  createSelector(
    // inputSelectors
    [
      (state: { user: UserState }) => state.user.roles,
      (state: { user: UserState }) => state.user.practiceRoles,
      () => practiceId,
      selectIsAffiliateRelations,
    ],
    // resultFunc
    (roles, practiceRoles, practiceId, isAffiliateRelations) => {
      const editPrivelegedPracticeRoles: PracticeRole[] = ["practice-manager"]
      if (adminCanEdit) {
        editPrivelegedPracticeRoles.push("practice-admin")
      }
      // add more optional roles here as needed
      let hasEditAuth = false
      if (isAffiliateRelations) {
        hasEditAuth = true
      } else if (roles.includes(AFFILIATE_ROLE) && practiceId !== null) {
        for (const practiceRole of practiceRoles) {
          if (
            practiceRole.practiceId === practiceId &&
            practiceRole.roles.some((role: PracticeRole) =>
              editPrivelegedPracticeRoles.includes(role),
            )
          ) {
            hasEditAuth = true
            continue
          }
        }
      }
      return hasEditAuth
    },
  )

export const userCanEditPractice = (
  practiceId: string,
  practiceStatus: PracticeStatus,
  adminCanEdit = false,
) =>
  createSelector(
    [
      (state) => selectIsAffiliateRelations(state),
      (state) => selectHasEditAuth(practiceId, adminCanEdit)(state),
    ],
    (isAffiliateRelations, hasEditRolePermissions) => {
      const isEditableStateForPracticeManager =
        editablePracticeStates.includes(practiceStatus)
      const isEditableStateForAffiliateRelations =
        practiceStatus === "pending-activation"
      const hasEditState =
        isEditableStateForPracticeManager ||
        (isAffiliateRelations && isEditableStateForAffiliateRelations)
      const hasEditAuth = hasEditRolePermissions && hasEditState
      return hasEditAuth
    },
  )

export const userCanViewProvider = (
  providerUserId: string | undefined,
  isOrphanedProvider: boolean = false,
) =>
  createSelector(
    [
      selectIsAffiliateRelations,
      selectAllPracticeRoles,
      (state: { user: UserState }) => state.user.id,
    ],
    (isAffiliateRelations, practiceRoles, currentUserId) => {
      const hasViewRole = practiceRoles
        .map((practiceRole) => practiceRole.roles)
        .some((roles) => roles.includes("provider"))
      return (
        isAffiliateRelations ||
        hasViewRole ||
        (isOrphanedProvider &&
          !!providerUserId &&
          currentUserId === providerUserId)
      )
    },
  )

export const userCanEditProvider = (
  providerProfileStatus: ProviderStatusDisplayId,
  providerUserId: string | undefined,
  isOrphanedProvider: boolean = false,
) =>
  createSelector(
    [
      selectIsAffiliateRelations,
      selectAllPracticeRoles,
      (state: { user: UserState }) => state.user.id,
    ],
    (isAffiliateRelations, practiceRoles, currentUserId) => {
      const hasEditRole =
        practiceRoles
          .map((practiceRole) => practiceRole.roles)
          .some((roles) => roles.includes("provider")) ||
        isAffiliateRelations ||
        (isOrphanedProvider &&
          !!providerUserId &&
          currentUserId === providerUserId)
      const editableStatuses: ProviderStatusDisplayId[] = ["pending", "active"]
      if (isOrphanedProvider) {
        editableStatuses.push("orphaned")
      }
      if (isAffiliateRelations) {
        editableStatuses.push("pending-activation")
      }
      const isEditableStatus = editableStatuses.includes(providerProfileStatus)

      return hasEditRole && isEditableStatus
    },
  )

export const selectUserIsAdminOrManager = (practiceId: string | null) =>
  createSelector(
    [selectIsAffiliate, selectAllPracticeRoles],
    (isAffiliate, allPracticeRoles) => {
      if (!isAffiliate) {
        return false
      }

      const practiceRoles = allPracticeRoles.find(
        (practiceRole) => practiceRole.practiceId === practiceId,
      )?.roles
      if (!practiceRoles) {
        return false
      }

      const isPracticeAdmin = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_ADMIN,
      )
      const isPracticeManager = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_MANAGER,
      )
      return isPracticeAdmin || isPracticeManager
    },
  )

export const selectUserIsAdminManagerOrBilling = (practiceId: string | null) =>
  createSelector(
    [selectIsAffiliate, selectAllPracticeRoles],
    (isAffiliate, allPracticeRoles) => {
      if (!isAffiliate) {
        return false
      }
      const practiceRoles = allPracticeRoles.find(
        (practiceRole) => practiceRole.practiceId === practiceId,
      )?.roles
      if (!practiceRoles) {
        return false
      }

      const isPracticeAdmin = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_ADMIN,
      )
      const isPracticeManager = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_MANAGER,
      )
      const isPracticeBilling = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_BILLING,
      )
      return isPracticeAdmin || isPracticeManager || isPracticeBilling
    },
  )

export const selectUserCanAccessFullPractice = (practiceId: string | null) =>
  createSelector(
    [selectIsAffiliateRelations, selectUserIsAdminManagerOrBilling(practiceId)],
    (isAffiliateRelations, isAdminManagerOrBilling) => {
      if (isAffiliateRelations) {
        return true
      }
      return isAdminManagerOrBilling
    },
  )

export const selectUserCanAccessPracticeAffiliates = (
  practiceId: string | null,
) =>
  createSelector(
    [selectIsAROrEapResearch, selectUserIsAdminManagerOrBilling(practiceId)],
    (isAROrEapResearch, isAdminManagerOrBilling) => {
      if (isAROrEapResearch) {
        return true
      }
      return isAdminManagerOrBilling
    },
  )

export const selectUserCanArchiveReferral = (practiceId: string | null) =>
  createSelector(
    [selectIsAROrEapResearch, selectAllPracticeRoles],
    (isAROrEapResearch, allPracticeRoles) => {
      if (isAROrEapResearch) {
        return true
      }

      const practiceRoles = allPracticeRoles.find(
        (practiceRole) => practiceRole.practiceId === practiceId,
      )?.roles
      if (!practiceRoles) {
        return false
      }

      const isPracticeAdmin = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_ADMIN,
      )
      const isPracticeManager = practiceRoles.includes(
        PracticeRoleEnum.PRACTICE_MANAGER,
      )
      const isProvider = practiceRoles.includes(PracticeRoleEnum.PROVIDER)
      return isPracticeAdmin || isPracticeManager || isProvider
    },
  )

export const selectUserIsAffiliateOrAffiliateRelations = () =>
  createSelector(
    [selectIsAffiliate, selectIsAffiliateRelations],
    (isAffiliate, isAffiliateRelations) => {
      return isAffiliate || isAffiliateRelations
    },
  )

export const selectSiteOutageMessage = (state: {
  user: UserState
}): SiteOutageMessageState | null => state.user.siteOutageMessage

export default userSlice.reducer
